/* eslint-disable max-lines */
import {
  Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, distinctUntilChanged, map, of, tap } from 'rxjs';

import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import {
  CheckboxFilter, CheckboxFilterVm, PickedRange, RangePicketVm, RangeResponseFilter
} from '@shared/filters/entities/fetch-filters.response';
import {
  MobileFilterMagicContComponent
} from '@shared/magic-filter-bar/mobile-filter-magic-cont/mobile-filter-magic-cont.component';
import { removeSubscriptions } from '@shared/helpers/shared-helpers';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { AirportsFilter } from '@flight-results/entities/request-search.payload';
import { pickedAirportCheckboxes, pickedCheckboxes, pickedRanges } from '@flight-results/mappers/filters.mapper';
import { FilterMagicStyle } from '@shared/magic-filter-bar/entities/filter-magic-style';
import { FilterApplier, FilterTypes, SectorTypes } from '@shared/flight-itinerary/entities/filters';
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { TripTypes } from '@app/entities/trip-types';
import { HourWidget, PriceWidget } from '@app/entities/flight-results-widget';
import { RangeFilterPayload } from '@shared/filters/entities/range-filter';
import { environment as ENV } from '@environment';
import { AirFilterBarService } from '@shared/magic-filter-bar/air-filter-bar/services/air-filter-bar.service';
import { PackagesResultFiltersFacade } from '@packages/packages-result/store/package-result-filters.facade';
import { PackagesResultFacade } from '@packages/packages-result/store/packages-result.facade';
import {
  pickedFlightPackageCheckboxes, pickedPackageFlightAirportCheckboxes, pickedPackageFlightRanges
} from '@packages/packages-result/mappers/flight-package-filters.mapper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import {
  isCheckBoxByAirFilter, isCheckRangeByAirFilter, isSectionCheckBoxByAirFilter
} from '@app/mappers/global-filters/air-global-filters.mapper';
import { HotelDestinationInputType, InputDestinationPlace } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { getDestinationByV2 } from '@packages/packages-search-bar/mappers/packages-url.mapper';

@Component({
  selector: 'app-individual-air-filter',
  templateUrl: './individual-air-filter.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class IndividualAirFilterContainer implements OnInit, OnDestroy {

  public filter: AirFilterVm;
  public isBySector: boolean;
  public isChanges: boolean;
  public isMobile: boolean;
  public isAirByPackage: boolean;

  public sortFligth = SorterTypes.Recommended;

  public pickedCheckboxes = [];
  public pickedAirportsCheckboxes = {} as AirportsFilter;
  public pickedRanges = {} as PickedRange;

  public readonly returnType = SectorTypes.Sector2;
  public readonly departureType = SectorTypes.Sector1;
  public readonly currencyCode = ENV.countryInfo.currency_code;

  protected hasChangeSortByFilter = false;

  private readonly subscriptions: Subscription[] = [];

  @ViewChild(MobileFilterMagicContComponent) mobileFilterMagicContComponent: MobileFilterMagicContComponent;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly packagesResultFiltersFacade: PackagesResultFiltersFacade,
    private readonly packagesResultsFacade: PackagesResultFacade,
    private readonly domManipulateService: DomManipulateService,
    private readonly flightResultsFacade: FlightResultsFacade,
    private readonly airFilterBarService: AirFilterBarService,
    private readonly searchFacade: BaseSearchBarFacade,
  ) {
    const currentNav = this.router.getCurrentNavigation();
    if (!currentNav || !currentNav?.extras) {
      return;
    }
    const routerState = currentNav?.extras?.state;
    this.filter = (routerState && routerState?.filter ? routerState?.filter : false);
    this.isMobile = (routerState && routerState?.isMobile ? routerState?.isMobile : false);
    this.isChanges = (routerState && routerState?.isChanges ? routerState?.isChanges : false);
    this.isBySector = (routerState && routerState?.isBySector ? routerState?.isBySector : false);
    this.isAirByPackage = (routerState && routerState?.isAirByPackage ? routerState?.isAirByPackage : false);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.pickedCheckboxes$.subscribe(checkboxes => {
        this.pickedCheckboxes = checkboxes;
      })
    );
    this.subscriptions.push(
      this.pickedAirportsCheckboxes$.subscribe(airports => {
        this.pickedAirportsCheckboxes = airports;
      })
    );
    this.subscriptions.push(
      this.pickedRanges$.subscribe(ranges => {
        this.pickedRanges = ranges;
      })
    );
    this.subscriptions.push(
      this.sortingHasBeenChanged$.pipe(
        distinctUntilChanged(),
        tap((sortingHasBeenChanged) => {
          this.hasChangeSortByFilter = sortingHasBeenChanged;
        })
      ).subscribe()
    );
    this.domManipulateService.setBodyScroll(false);
  }

  ngOnDestroy(): void {
    removeSubscriptions(this.subscriptions);
    this.domManipulateService.setBodyScroll(true);
  }

  get destinationPlace$(): Observable<InputDestinationPlace> {
    return this.isAirByPackage ? this.searchFacade.packageDestinationPlace$ : of(null);
  }

  private get pickedCheckboxes$(): Observable<string[]> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.flightsPayload$.pipe(map(payload => pickedFlightPackageCheckboxes(payload))) :
      this.flightResultsFacade.searchPayload$.pipe(map(payload => pickedCheckboxes(payload)));
  }

  private get pickedAirportsCheckboxes$(): Observable<AirportsFilter> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.flightsPayload$.pipe(map(payload => pickedPackageFlightAirportCheckboxes(payload))) :
      this.flightResultsFacade.searchPayload$.pipe(map(payload => pickedAirportCheckboxes(payload)));
  }

  private get sortingHasBeenChanged$(): Observable<boolean> {
    return this.airFilterBarService.sortingHasBeenChangedByAir$;
  }

  get pickedRanges$(): Observable<PickedRange> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.flightsPayload$.pipe(map(payload => pickedPackageFlightRanges(payload))) :
      this.flightResultsFacade.searchPayload$.pipe(map(payload => pickedRanges(payload)));
  }

  get orderCriteria$(): Observable<SorterTypes> {
    return this.isAirByPackage ? this.packagesResultFiltersFacade.airOrderCriteria$ : this.flightResultsFacade.orderCriteria$;
  }

  get orderCriteriaSectorNumber$(): Observable<number> {
    return this.isAirByPackage ? this.packagesResultFiltersFacade.airOrderCriteriaSectorNumber$ :
      this.flightResultsFacade.orderCriteriaSectorNumber$;
  }

  get orderDesc$(): Observable<boolean> {
    return this.isAirByPackage ? this.packagesResultFiltersFacade.airOrderDesc$ : this.flightResultsFacade.orderDesc$;
  }

  get filterMagicContStyle(): FilterMagicStyle {
    return this.isSorter(this.filter) ? FilterMagicStyle.StyleOne : FilterMagicStyle.StyleTwo;
  }

  get hasActiveFilter(): boolean {
    if (this.isCheckboxFilter(this.filter)) {
      return !!(this.filter.value as CheckboxFilterVm[]).find(value => this.pickedCheckboxes.includes(value.Text));
    } else if (this.isRangeFilter(this.filter)) {
      return !!this.checkedRanges(this.filter.type);
    } else if (this.isSorter(this.filter)) {
      return this.hasChangeSortByFilter;
    } else {
      return false;
    }
  }

  private get infoPriceWidgets$(): Observable<PriceWidget[]> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.packageAirWidgets$.pipe(map(packageAirWidgets => packageAirWidgets.PriceWidget)) :
      this.flightResultsFacade.infoPriceWidgets$;
  }

  private get infoDepartureWidget$(): Observable<HourWidget[]> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.packageAirWidgets$.pipe(map(packageAirWidgets => packageAirWidgets.HourWidget)) :
      this.flightResultsFacade.infoDepartureWidget$;
  }

  private get infoDepartureWidgetReturn$(): Observable<HourWidget[]> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.packageAirWidgets$.pipe(map(packageAirWidgets => packageAirWidgets.HourWidgetReturn)) :
      this.flightResultsFacade.infoDepartureWidgetReturn$;
  }

  private get infoFlightDurationWidget$(): Observable<HourWidget[]> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.packageAirWidgets$.pipe(map(packageAirWidgets => packageAirWidgets.FlightDurationWidget)) :
      this.flightResultsFacade.infoFlightDurationWidget$;
  }

  private get infoFlightDurationWidgetReturn$(): Observable<HourWidget[]> {
    return this.isAirByPackage ?
      this.packagesResultsFacade.packageAirWidgets$.pipe(map(packageAirWidgets => packageAirWidgets.FlightDurationWidgetReturn)) :
      this.flightResultsFacade.infoFlightDurationWidgetReturn$;
  }

  public rangeFilter$(type: FilterTypes, sector?: TripTypes): Observable<PriceWidget[] | HourWidget[]> {
    switch (type) {
      case FilterTypes.PriceRange:
        return this.infoPriceWidgets$;
      case FilterTypes.DepartureTimes:
        return sector === TripTypes.DEPARTURE ? this.infoDepartureWidget$ : this.infoDepartureWidgetReturn$;
      case FilterTypes.SectorDurations:
        return sector === TripTypes.DEPARTURE ? this.infoFlightDurationWidget$ : this.infoFlightDurationWidgetReturn$;
      default:
        return of(null);
    }
  }

  public isSorter(item: AirFilterVm): boolean {
    return item.type === FilterTypes.AirFilterTabs && this.isMobile;
  }

  public isCheckboxFilter(item: AirFilterVm): boolean {
    return isCheckBoxByAirFilter(item.type) && (item.value as CheckboxFilter[]).length > 0;
  }

  public isRangeFilter(item: AirFilterVm): boolean {
    return isCheckRangeByAirFilter(item.type);
  }

  public isSectionCheckBoxFilter(item: AirFilterVm): boolean {
    return isSectionCheckBoxByAirFilter(item.type);
  }

  public valuesIsArray(item: AirFilterVm): boolean {
    return Array.isArray(item.value);
  }

  public checkedRanges(type: string): string[] | number[] | RangePicketVm {
    if (!isNullOrUndefined(this.pickedRanges)) {
      switch (type) {
        case FilterTypes.PriceRange:
          return !isEmpty(this.pickedRanges[type]) ? this.pickedRanges[type] : null;
        case FilterTypes.DepartureTimes:
        case FilterTypes.SectorDurations:
          if (this.pickedRanges[type]) {
            const ranges = Object.values(this.pickedRanges[type]).filter(obj => !isEmpty(obj));
            return ranges.length > 0 ? ranges : null;
          } else {
            return null;
          }
        default:
          return null;
      }
    } else {
      return null;
    }
  }

  public getItemLabel(label: string, isTitle: boolean | undefined = false): string {
    return this.airFilterBarService.getItemLabel(label, isTitle);
  }

  public clearIndividualFilter(): void {
    const payload = { type: this.filter.type, value: null, removable: true };
    this.hasChangeSortByFilter = false;
    if (this.filter.type === FilterTypes.AirFilterTabs) {
      this.airFilterBarService.sortingHasBeenChangedByAir(false);
    }
    if (this.isAirByPackage) {
      this.applyAirFilterVmByPackage({
        ...payload,
        value: this.filter.type === FilterTypes.AirFilterTabs ? SorterTypes.Recommended : null
      });
    } else {
      this.searchFlightsWithFilter(payload);
    }
  }

  public applyRangeFilter(type: FilterTypes, item: RangeResponseFilter, event: RangeFilterPayload): void {
    const { range, removable } = event;
    const apply = this.isAirByPackage ? this.applyAirFilterVmByPackage.bind(this) : this.searchFlightsWithFilter.bind(this);
    if (type === FilterTypes.SectorDurations || type === FilterTypes.DepartureTimes) {
      apply({ type: type as FilterTypes, value: range, removable, sector: item.SectorNumber });
    } else {
      apply({ type: type as FilterTypes, value: range, removable });
    }
  }

  public applySorterFilterVm(filter: FilterApplier): void {
    this.airFilterBarService.sortingHasBeenChangedByAir(true);
    if (this.isAirByPackage) {
      this.applyAirFilterVmByPackage(filter);
    } else {
      this.searchFlightsWithFilter(filter);
    }
  }

  public checkboxFilterAction(filter: FilterApplier): void {
    if (this.isAirByPackage) {
      this.applyAirFilterVmByPackage(filter);
    } else {
      this.searchFlightsWithFilter(filter);
    }
  }

  public searchFlightsWithFilter(filter: FilterApplier): void {
    this.trackSelectFilterEvent(filter);
    if (this.isBySector && this.isMobile) {
      this.flightResultsFacade.searchFlightsWithSingleFilterBySector(filter);
    } else {
      this.flightResultsFacade.searchFlightsWithSingleFilter([filter], this.isMobile);
    }
    this.domManipulateService.resetScrollPosition();
  }

  private trackSelectFilterAmplitudeEvent(filter: FilterApplier): void {
    const searchBy = this.isAirByPackage ? SearchBy.PACKAGES : SearchBy.FLIGHTS;
    this.airFilterBarService.trackSelectFilterAmplitudeEvent(searchBy, filter);
  }

  public getCriteria(type: FilterTypes, text: string, destination?: InputDestinationPlace): string {
    const params = this.isMobile ? this.route.parent.snapshot.params : this.route.snapshot.params;
    const destinationByV2 = this.isAirByPackage ? getDestinationByV2(params.destination) : null;
    const selectedIds = params?.selected_ids?.split('-') || [];
    const departure = selectedIds.length > 0 ? params.arrival : params.departure;
    const arrival = selectedIds.length > 0 ? params.departure : this.isAirByPackage ? (
      destinationByV2?.Type === HotelDestinationInputType.hotel ? destination?.NearestIata : destinationByV2.Code
    ) : params.arrival;
    return this.airFilterBarService.getCriteriaByLabel(departure, arrival, type, text);
  }

  public getItemLabelProps(type: string): { [key: string]: string } {
    if (type === FilterTypes.PriceRange) {
      return { currency: this.currencyCode };
    } else {
      return {};
    }
  }

  private applyAirFilterVmByPackage(filter: FilterApplier): void {
    this.trackSelectFilterEvent(filter);
    this.domManipulateService.resetScrollPosition();
    this.packagesResultsFacade.fetchAirWithFilter(filter, true);
  }

  private trackSelectFilterEvent(filter: FilterApplier): void {
    if (
      (!isNullOrUndefined(filter.removable) && !filter.removable) ||
      (isNullOrUndefined(filter.removable) && !isEmpty(filter.value))
    ) {
      this.trackSelectFilterAmplitudeEvent(filter);
      if (filter.type === FilterTypes.AirFilterTabs) {
        this.mobileFilterMagicContComponent?.close();
      }
    }
  }

}
